/// <reference types="@shopify/polaris-types" />
/// <reference types="@shopify/app-bridge-types" />

import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import "@shopify/polaris/build/esm/styles.css";
import { NavMenu } from "@shopify/app-bridge-react";
import { MainLayout } from "./layouts/MainLayout";
import { AuthGuard } from "./components/AuthGuard/AuthGuard";
import { CanvasLayout } from "./layouts/CanvasLayout";
import { EditorErrorBoundary } from "./components/common/EditorErrorBoundary";
import WelcomePage from "./pages/WelcomePage";
import ButtonEditorPage from "./pages/ButtonEditorPage";
import ShippingTaxesEditorPage from "./pages/ShippingTaxesEditorPage";
import TaxEditorPage from "./pages/TaxEditorPage";
import SecuritySettingsEditorPage from "./pages/SecuritySettingsEditorPage";
import OtpConfigPage from "./pages/security/OtpConfigPage";
import IntegrationSettingsPage from "./pages/IntegrationsSettingsPage";
import GoogleOAuthCallbackPage from "./pages/GoogleOAuthCallbackPage";
import BillingPage from "./pages/BillingPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import AddressValidatorPage from "./pages/address-validator/AddressValidatorPage";
import SalesBoosterPage from "./pages/SalesBoosterPage";
import UpsellsOneClickPage from "./pages/sales-booster/UpsellsOneClickPage";
import OneClickEditor from "./pages/sales-booster/one-click-editor/OneClickEditor";
import UpsellsOneTickPage from "./pages/sales-booster/UpsellsOneTickPage";
import OneTickEditor from "./pages/sales-booster/one-tick-editor/OneTickEditor";
import QuantityOffersPage from "./pages/sales-booster/QuantityOffersPage";
import QuantityTiersEditor from "./pages/sales-booster/quantity-tiers-editor/QuantityTiersEditor";
import BundleOffersPage from "./pages/sales-booster/BundleOffersPage";
import BundleEditor from "./pages/sales-booster/bundle-editor/BundleEditor";
import DownsellsPage from "./pages/sales-booster/DownsellsPage";
import DownsellEditor from "./pages/sales-booster/downsell-editor/DownsellEditor";
import PixelsPage from "./pages/PixelsPage";

const isEmbedded = window.top !== window.self;
const isDev = import.meta.env.DEV;

function Root() {
  const bypassShopifyContext = isDev && !isEmbedded;

  return (
    <>
      {!bypassShopifyContext && (
        <NavMenu>
          <a href="/" rel="home">
            Home
          </a>
          <a href="/button-editor">Botón de Compra</a>
          <a href="/editor">Editor</a>
          <a href="/tax-editor">Editor de Impuestos</a>
          {/* <a href="/billing">Plan y Facturación</a> */}
          <a href="/security-editor">Bloqueo de Usuario</a>
          <a href="/integrations">Integraciones</a>
          <a href="/analytics">Dashboard de analíticas</a>
          <a href="/sales-booster">Impulsor de Ventas</a>
          <a href="/pixels">Píxeles</a>
          <a href="/autocomplete">Validador de Direcciones</a>
        </NavMenu>
      )}

      {/* Bypass del AuthGuard en local para poder ver la UI sin que falle el token */}
      {bypassShopifyContext ? (
        <div
          style={{ padding: "20px", background: "#f4f6f8", minHeight: "100vh" }}
        >
          <div
            style={{
              background: "#ffea8a",
              padding: "10px",
              textAlign: "center",
              marginBottom: "20px",
              borderRadius: "8px",
              color: "#8a6116",
              fontWeight: "bold",
            }}
          >
            Modo Desarrollo Local (Standalone) - AuthGuard Desactivado
            {" · "}
            <a href="/analytics" style={{ color: "#8a6116" }}>
              Dashboard de Analíticas
            </a>
          </div>
          <Outlet />
        </div>
      ) : (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      )}
    </>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        path: "/",
        element: <MainLayout />,
        children: [{ index: true, element: <WelcomePage /> }],
      },
      {
        path: "/editor",
        element: (
          <EditorErrorBoundary>
            <CanvasLayout />
          </EditorErrorBoundary>
        ),
      },
      {
        path: "/forms",
        element: (
          <EditorErrorBoundary>
            <CanvasLayout />
          </EditorErrorBoundary>
        ),
      },
      {
        path: "/button-editor",
        element: <ButtonEditorPage />,
      },
      {
        path: "/shipping-editor",
        element: <ShippingTaxesEditorPage />,
      },
      {
        path: "/tax-editor",
        element: <TaxEditorPage />,
      },
      {
        path: "/security-editor",
        element: <SecuritySettingsEditorPage />,
      },
      {
        path: "/security-editor/otp",
        element: <OtpConfigPage />,
      },
      {
        path: "/integrations",
        element: <IntegrationSettingsPage />,
      },
      {
        path: "/auth/google/callback",
        element: <GoogleOAuthCallbackPage />,
      },
      {
        path: "/billing",
        element: <BillingPage />,
      },
      {
        path: "/analytics",
        element: <AnalyticsDashboardPage />,
      },
      {
        path: "/autocomplete",
        element: <AddressValidatorPage />,
      },
      {
        path: "/sales-booster",
        element: <SalesBoosterPage />,
      },
      {
        path: "/pixels",
        element: <PixelsPage />,
      },
      {
        path: "/sales-booster/upsells-1-click",
        element: <UpsellsOneClickPage />,
      },
      {
        path: "/sales-booster/upsells-1-click/new",
        element: <OneClickEditor />,
      },
      {
        path: "/sales-booster/upsells-1-click/:id",
        element: <OneClickEditor />,
      },
      {
        path: "/sales-booster/upsells-1-tick",
        element: <UpsellsOneTickPage />,
      },
      {
        path: "/sales-booster/upsells-1-tick/new",
        element: <OneTickEditor />,
      },
      {
        path: "/sales-booster/upsells-1-tick/:id",
        element: <OneTickEditor />,
      },
      {
        path: "/sales-booster/downsells",
        element: <DownsellsPage />,
      },
      {
        path: "/sales-booster/downsells/new",
        element: <DownsellEditor />,
      },
      {
        path: "/sales-booster/downsells/:id",
        element: <DownsellEditor />,
      },
      {
        path: "/sales-booster/quantity-offers",
        element: <QuantityOffersPage />,
      },
      {
        path: "/sales-booster/quantity-offers/new",
        element: <QuantityTiersEditor />,
      },
      {
        path: "/sales-booster/quantity-offers/:id",
        element: <QuantityTiersEditor />,
      },
      {
        path: "/sales-booster/bundles",
        element: <BundleOffersPage />,
      },
      {
        path: "/sales-booster/bundles/new",
        element: <BundleEditor />,
      },
      {
        path: "/sales-booster/bundles/:id",
        element: <BundleEditor />,
      },
    ],
  },
]);

export default function App() {
  if (isDev && !isEmbedded) {
    return <RouterProvider router={router} />;
  }

  if (!isEmbedded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "system-ui",
        }}
      >
        <h1>Codway App</h1>
        <p style={{ color: "#666" }}>
          Esta aplicación es exclusiva para administradores de Shopify.
        </p>
      </div>
    );
  }

  const apiKey = process.env.SHOPIFY_API_KEY;

  if (!apiKey) {
    return (
      <div style={{ padding: "20px", background: "#ffebee", color: "#c62828" }}>
        <h1>Error de Configuración</h1>
        <p>No se encontró la SHOPIFY_API_KEY.</p>
        <p>Asegúrate de iniciar la app desde Shopify CLI.</p>
      </div>
    );
  }

  return <RouterProvider router={router} />;
}
